import { useEffect, useRef, useState } from 'react';

// existing imports
import EditAddressForm from '../../../forms/teamForms/editAddress';
import EditSubscriptionsForm from '../../../forms/teamForms/editSubscription';
import EditDiscountsForm from '../../../forms/teamForms/editDiscounts';
import EditTagsForm from '../../../forms/teamForms/editTags';
import EditBrandForm from '../../../forms/teamForms/editBrand';
import EditAvailabilityForm from '../../../forms/teamForms/editAvailability';
import EditJoinFormConfig from '../../../forms/teamForms/editJoinFormConfig';
import EditProfileFieldsForm from '../../../forms/teamForms/editProfileFields';
import EditMetadataForm from '../../../forms/teamForms/editMetadata';
import EditPublicLinksForm from '../../../forms/teamForms/editPublicLinks';
import EditDocumentsForm from '../../../forms/teamForms/editDocuments';
import EditPendingMembersForm from '../../../forms/teamForms/editPendingMembers';
import EditWaitlistMembersForm from '../../../forms/teamForms/editWaitlistMembers';
import EditMemberForm from '../../../forms/teamForms/editMember';
import EditModulesForm from '../../../forms/teamForms/editModules';
import EditAdminPermissionsForm from '../../../forms/teamForms/editAdminPermissions';
import EditBoardGifSettingsForm from '../../../forms/teamForms/editBoardGifSettings';
import InviteMembersForm from '../../../forms/teamForms/inviteMembersForm';
import ImportMembersForm from '../../../forms/teamForms/importMembersForm';
import ExportMembersForm from '../../../forms/teamForms/exportMembersForm';
import MemberProfileFieldsForm from '../../../forms/teamForms/memberProfileFieldsForm';
import EditBoltOnsForm from '../../../forms/teamForms/editBoltOns'; 
import EditRowingBoatsForm from '../../../forms/teamForms/rowing/editBoats';
import EditBoatBookingRulesForm from '../../../forms/teamForms/rowing/editBookingRules';
import EditOutingsForm from '../../../forms/teamForms/rowing/editOutings';
import EditLearnToRowForm from '../../../forms/teamForms/rowing/editLearnToRow';
import EditLearnToSailForm from '../../../forms/teamForms/sailing/editLearnToSail';
import EditDutyRosterForm from '../../../forms/teamForms/sailing/editDutyRoster';
import EditMooringsForm from '../../../forms/teamForms/sailing/editMoorings';
import EditSailingBoatsForm from '../../../forms/teamForms/sailing/editBoats';
import EditSailingOutingsForm from '../../../forms/teamForms/sailing/editOutings';
import EditSailingCrewFinderForm from '../../../forms/teamForms/sailing/editCrewFinder';
import RequestSailingBoatForm from '../../../forms/teamForms/sailing/requestBoat';
import RequestBoatForm from '../../../forms/teamForms/rowing/requestBoat';
import EventSetupForm from '../../../forms/teamForms/events/eventSetupForm';
import EditCoursesForm from '../../../forms/teamForms/courses/editCourses';
import EditCourseBoatReservations from '../../../forms/teamForms/courses/editCourseBoatReservations';
import { filterEditableTeamTags as filterLearnToRowEditableTeamTags } from '../../utils/learnToRow';
import { isLearnToSailSystemTag } from '../../utils/learnToSail';
import { getMembershipHandling } from '../../../../utils/membershipHandling';

function filterEditableTeamTags(tags = []) {
  return filterLearnToRowEditableTeamTags(tags).filter((tag) => !isLearnToSailSystemTag(tag));
}

const MODAL_LABELS = {
  brand: 'Edit branding',
  courses: 'Manage courses',
  courseResources: 'Manage course resources',
  sailingBoats: 'Manage club boats',
  adminPermissions: 'Manage admin permissions',
  modules: 'Manage enabled modules',
};


export default function TeamEditModal({
  section,
  data,
  onClose,
  onSave,
  teamId,
  team,
  user,
  memberTags,
  memberProfiles = [],
  canManageFinance = false,
  canManageMembership = false,
  isFullAdmin = false,
}) {
  const [localData, setLocalData] = useState(data ?? {});
  const [learnToRowHasUnsavedGeneralChanges, setLearnToRowHasUnsavedGeneralChanges] = useState(false);
  const [learnToSailHasUnsavedGeneralChanges, setLearnToSailHasUnsavedGeneralChanges] = useState(false);
  const [coursesHaveUnsavedChanges, setCoursesHaveUnsavedChanges] = useState(false);
  const [boltOnsBusy, setBoltOnsBusy] = useState(false);
  const [tagsHaveUnsavedChanges, setTagsHaveUnsavedChanges] = useState(false);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  useEffect(() => {
    setLocalData(data ?? {});
    setBoltOnsBusy(false);
    setLearnToRowHasUnsavedGeneralChanges(false);
    setLearnToSailHasUnsavedGeneralChanges(false);
    setCoursesHaveUnsavedChanges(false);
    setTagsHaveUnsavedChanges(false);
  }, [data, section]);

  const handleClose = ({ discardUnsavedChanges = false } = {}) => {
    if (section === 'boltOns' && boltOnsBusy) return;
    if (section === 'tags' && tagsHaveUnsavedChanges && !discardUnsavedChanges && !window.confirm('Your latest tag changes have not saved yet. Leave without saving them?')) return;
    if (
      section === 'learnToRow' &&
      learnToRowHasUnsavedGeneralChanges &&
      !window.confirm('You have unsaved Learn to Row general setup changes. Leave without saving them?')
    ) {
      return;
    }
    if (
      section === 'learnToSail' &&
      learnToSailHasUnsavedGeneralChanges &&
      !window.confirm('You have unsaved Learn to Sail general setup changes. Leave without saving them?')
    ) {
      return;
    }
    if (section === 'courses' && coursesHaveUnsavedChanges && !window.confirm('You have unsaved course changes. Leave without saving them?')) return;
    setLearnToRowHasUnsavedGeneralChanges(false);
    setLearnToSailHasUnsavedGeneralChanges(false);
    setCoursesHaveUnsavedChanges(false);
    setTagsHaveUnsavedChanges(false);
    onClose?.();
  };
  closeRef.current = handleClose;

  useEffect(() => {
    if (!section) return undefined;
    const dialog = dialogRef.current;
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusableSelector = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    const focusableElements = () => Array.from(dialog?.querySelectorAll(focusableSelector) || [])
      .filter((element) => element.getAttribute('aria-hidden') !== 'true');

    (focusableElements()[0] || dialog)?.focus();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const elements = focusableElements();
      if (elements.length === 0) {
        event.preventDefault();
        dialog?.focus();
        return;
      }
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) previouslyFocused.focus();
    };
  }, [section]);

  if (!section) return null;

  const forms = {
    address: <EditAddressForm isOpen initialAddress={localData} onClose={handleClose} onSave={onSave} />,
    subscriptions: <EditSubscriptionsForm isOpen initialSubscriptions={localData} onClose={handleClose} onSave={onSave} teamId={teamId} membershipMode={getMembershipHandling(team || {}).mode} />,
    discounts: <EditDiscountsForm isOpen teamId={teamId} onClose={handleClose} onSave={onSave} />,
    tags: <EditTagsForm isOpen initialTags={localData} onClose={handleClose} onSave={onSave} teamId={teamId} onUnsavedChangesChange={setTagsHaveUnsavedChanges} />,
    boltOns: <EditBoltOnsForm isOpen teamId={teamId} onClose={() => { setBoltOnsBusy(false); onClose?.(); }} onBusyChange={setBoltOnsBusy} />,
    brand: <EditBrandForm isOpen editClubUrl initialBrand={localData} initialClubUrlSlug={team?.clubUrlSlug || ''} onClose={handleClose} onSave={onSave} teamId={teamId} />,
    availability: <EditAvailabilityForm isOpen initialAvailability={localData} onClose={handleClose} onSave={onSave} />,
    joinFormConfig: <EditJoinFormConfig teamId={teamId} isOpen onClose={handleClose} onSave={onSave} />,
    profileFieldsConfig: (
      <EditProfileFieldsForm
        teamId={teamId}
        isOpen
        onClose={handleClose}
        onSave={onSave}
      />
    ),
    teamMetadata: (
      <EditMetadataForm
        isOpen
        initialMetadata={localData}
        initialName={team?.name || ''}
        initialDescription={team?.description || ''}
        onClose={handleClose}
        onSave={onSave}
      />
    ),
    publicLinks: <EditPublicLinksForm isOpen initialLinks={localData} onClose={handleClose} onSave={onSave} />,
    documents: <EditDocumentsForm isOpen teamId={teamId} onClose={handleClose} />,
    pendingMembers: (
      <EditPendingMembersForm
        isOpen
        teamId={teamId}
        onClose={handleClose}
        onSave={onSave}
        allTags={team?.tags || []}
        teamName={team?.name || 'your team'}
        canManageFinance={canManageFinance || isFullAdmin}
        adminRoles={team?.adminRoles || []}
        adminAssignments={team?.adminAssignments || []}
      />
    ),
    waitlist: (
      <EditWaitlistMembersForm
        isOpen
        teamId={teamId}
        teamName={team?.name || 'your team'}
        onClose={handleClose}
        onSave={onSave}
      />
    ),
    member: (
      <EditMemberForm
        teamId={teamId}
        member={localData}
        allTags={filterEditableTeamTags(team?.tags || [])}
        memberProfiles={memberProfiles}
        onClose={handleClose}
        onSave={onSave}
        teamName={team?.name || 'your team'}
      />
    ),
    modules: (
      <EditModulesForm
        isOpen
        initialModules={localData}
        metadata={team?.teamMetadata || {}}
        isFullAdmin={isFullAdmin}
        onClose={handleClose}
        onSave={onSave}
      />
    ),
    adminPermissions: (
      <EditAdminPermissionsForm
        isOpen
        teamId={teamId}
        roles={team?.adminRoles || []}
        assignments={team?.adminAssignments || []}
        members={memberProfiles}
        fullAdminIds={team?.admins || []}
        modules={team?.modules || {}}
        metadata={team?.teamMetadata || {}}
        onClose={handleClose}
        onSave={onSave}
      />
    ),
    boardGifSettings: (
      <EditBoardGifSettingsForm
        isOpen
        initialSettings={localData?.gifs || localData}
        initialBoardSettings={localData?.boards || team?.communicationSettings?.boards || {}}
        onClose={handleClose}
        onSave={onSave}
      />
    ),
    inviteMembers: (
      <InviteMembersForm
        isOpen
        onClose={handleClose}
        teamTags={filterEditableTeamTags(team?.tags || [])}
        teamId={teamId}
        teamName={team?.name || ''}
        adminUid={user?.uid}
      />
    ),
    importMembers: (
      <ImportMembersForm
        teamTags={team?.tags || []}
        isOpen
        onClose={handleClose}
        teamId={teamId}
        canApproveMembership={canManageMembership}
        canApproveFinance={canManageFinance}
      />
    ),
    exportMembers: (
      <ExportMembersForm
        isOpen
        teamId={teamId}
        canExportFinancialFields={canManageFinance}
        onClose={handleClose}
      />
    ),
    memberProfileFields: (
      <MemberProfileFieldsForm
        isOpen
        teamId={teamId}
        userId={user?.uid}
        onClose={handleClose}
        config={team?.profileFieldsConfig}
      />
    ),
    adminMemberProfileFields: (
      <MemberProfileFieldsForm
        isOpen
        teamId={teamId}
        userId={localData?.id}
        memberName={localData?.name || 'Member'}
        mode="admin"
        onClose={handleClose}
        config={team?.profileFieldsConfig}
      />
    ),

    // 🚤 Boats: the form loads/saves the subcollection itself.
    rowingBoats: (
      <EditRowingBoatsForm
        isOpen
        allTags={filterEditableTeamTags(team?.tags || []).filter((t) => t?.name)}
        onClose={handleClose}
        onSave={onSave}
        teamId={teamId}
        focusBoatId={localData?.focusBoatId || null}
      />
    ),

    // 📜 Rules stay on the team document
    boatBookingRules: (
      <EditBoatBookingRulesForm
        isOpen
        initialRules={localData}
        teamTags={filterEditableTeamTags(team?.tags || []).filter((t) => t?.name)}
        onClose={handleClose}
        onSave={onSave}
      />
    ),
    sailingBoatBookingRules: (
      <EditBoatBookingRulesForm
        isOpen
        initialRules={localData}
        title="Sailing Boat Booking Rules"
        rulesDefaults={{ releaseDayOfWeek: 1 }}
        teamTags={filterEditableTeamTags(team?.tags || []).filter((t) => t?.name)}
        onClose={handleClose}
        onSave={onSave}
      />
    ),

    outings: (
      <EditOutingsForm
        isOpen
        teamId={teamId}
        teamTags={filterEditableTeamTags(team?.tags || []).filter((t) => t?.name)}
        rules={team?.rowing?.rules || {}}
        onClose={handleClose}
        onSave={onSave}
        focusOutingId={localData?.outingId || null}
        createNew={Boolean(localData?.createNew)}
        focusPresets={Boolean(localData?.managePresets)}
      />
    ),
    learnToRow: (
      <EditLearnToRowForm
        isOpen
        teamId={teamId}
        team={team}
        onClose={handleClose}
        onUnsavedGeneralChangesChange={setLearnToRowHasUnsavedGeneralChanges}
        focusCourseId={localData?.focusCourseId || null}
        createNew={Boolean(localData?.createNew)}
        initialPanel={localData?.initialPanel || 'courses'}
      />
    ),
    learnToSail: (
      <EditLearnToSailForm
        isOpen
        teamId={teamId}
        team={team}
        onClose={handleClose}
        onUnsavedGeneralChangesChange={setLearnToSailHasUnsavedGeneralChanges}
        focusCourseId={localData?.focusCourseId || null}
        createNew={Boolean(localData?.createNew)}
        initialPanel={localData?.initialPanel || 'courses'}
      />
    ),
    courses: (
      <EditCoursesForm
        teamId={teamId}
        teamMetadata={team?.teamMetadata || {}}
        focusCourseId={localData?.focusCourseId || ''}
        createNew={Boolean(localData?.createNew)}
        canManageBookingForms={localData?.canManageBookingForms === true}
        onUnsavedChangesChange={setCoursesHaveUnsavedChanges}
        onClose={handleClose}
      />
    ),
    courseResources: (
      <EditCourseBoatReservations
        teamId={teamId}
        courseType={localData?.courseType || 'generic'}
        courseId={localData?.courseId || ''}
        onClose={handleClose}
      />
    ),
    dutyRoster: (
      <EditDutyRosterForm
        isOpen
        teamId={teamId}
        team={team}
        userId={user?.uid || ''}
        isAdmin={Boolean(localData?.isAdmin)}
        initialTab={localData?.initialTab || ''}
        teamTags={(team?.tags || []).filter((t) => t?.type !== 'ageRule' && t?.name)}
        memberProfiles={memberProfiles}
        onClose={handleClose}
      />
    ),
    moorings: (
      <EditMooringsForm
        isOpen
        teamId={teamId}
        team={team}
        memberProfiles={memberProfiles}
        initialTab={localData?.initialTab || 'map'}
        onClose={handleClose}
      />
    ),
    sailingBoats: (
      <EditSailingBoatsForm
        isOpen
        allTags={filterEditableTeamTags(team?.tags || []).filter((t) => t?.name)}
        onClose={handleClose}
        onSave={onSave}
        teamId={teamId}
        focusBoatId={localData?.focusBoatId || null}
      />
    ),
    sailingOutings: (
      <EditSailingOutingsForm
        isOpen
        teamId={teamId}
        teamTags={filterEditableTeamTags(team?.tags || []).filter((t) => t?.name)}
        rules={{ ...(team?.sailing?.boats?.rules || {}), ...(team?.sailing?.rules || {}) }}
        onClose={handleClose}
        onSave={onSave}
        focusOutingId={localData?.outingId || null}
        createNew={Boolean(localData?.createNew)}
        focusPresets={Boolean(localData?.managePresets)}
      />
    ),
    sailingCrewFinder: (
      <EditSailingCrewFinderForm
        isOpen
        teamId={teamId}
        team={team}
        user={user}
        memberProfile={memberProfiles.find((member) => member.id === user?.uid) || null}
        memberTags={memberTags ?? user?.tags ?? []}
        memberProfiles={memberProfiles}
        teamTags={filterEditableTeamTags(team?.tags || []).filter((t) => t?.name)}
        isAdmin={Boolean(localData?.isAdmin)}
        initialTab={localData?.initialTab || 'crew_needed'}
        onClose={handleClose}
      />
    ),

    // 📝 Requests: avoid mirrored resources; let the form fetch boats by teamId
    boatRequest: (
      <RequestBoatForm
        isOpen
        teamId={teamId}
        memberId={user?.uid}
        rules={team?.rowing?.rules || {}}
        onClose={handleClose}
        onSave={onSave}
        userTags={memberTags ?? user?.tags ?? []}
      />
    ),
    sailingBoatRequest: (
      <RequestSailingBoatForm
        isOpen
        teamId={teamId}
        memberId={user?.uid}
        onClose={handleClose}
        onSave={onSave}
        userTags={memberTags ?? user?.tags ?? []}
      />
    ),
    eventSetup: (
      <EventSetupForm
        isOpen
        teamId={teamId}
        teamTags={(team?.tags || []).filter((t) => t?.type !== 'ageRule' && t?.name)}
        userId={user?.uid || null}
        onClose={handleClose}
        onSave={onSave}
      />
    ),
  };

  const brand = team?.brand || {};

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={MODAL_LABELS[section] || 'Edit club settings'}
        tabIndex={-1}
        className={
          section === 'brand'
            ? 'modal-content modal-content--branding'
          : section === 'sailingCrewFinder'
            ? 'modal-content modal-content--xl modal-content--crew-finder'
          : section === 'outings' || section === 'sailingOutings' || section === 'learnToRow' || section === 'learnToSail'
            ? 'modal-content modal-content--xl modal-content--outings'
          : section === 'courses'
            ? 'modal-content modal-content--course-workspace'
            : section === 'courseResources'
              ? 'modal-content modal-content--xl'
            : section === 'dutyRoster'
              ? 'modal-content modal-content--xl modal-content--duty-roster'
            : section === 'moorings'
              ? 'modal-content modal-content--xl modal-content--moorings'
            : section === 'adminPermissions'
              ? 'modal-content modal-content--xl modal-content--permissions'
            : section === 'importMembers'
              ? 'modal-content modal-content--xl'
            : section === 'boltOns'
              ? 'modal-content modal-content--xl modal-content--bolt-ons'
            : section === 'sailingBoats'
              ? 'modal-content modal-content--xl modal-content--sailing-boats'
              : section === 'eventSetup'
                ? 'modal-content modal-content--xl'
              : 'modal-content'
        }
        onClick={e => e.stopPropagation()}
        style={{
          ...(brand.primaryColor     && { "--team-primary": brand.primaryColor }),
          ...(brand.secondaryColor   && { "--team-secondary": brand.secondaryColor }),
          ...(brand.headingTextColor && { "--team-heading-text": brand.headingTextColor }),
          ...(brand.bodyTextColor    && { "--team-body-text": brand.bodyTextColor }),
        }}
      >
        {forms[section]}
      </div>
    </div>
  );
}
